import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [previewMedia] - The live preview in a desktop media grid.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener('change', (event) => {
      if (event.target?.matches('[data-studio-selector]')) this.#handleVariantSelected();
    }, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Shows the live preview when an option changes its appearance.
   */
  #handleVariantSelected = () => {
    const preview = this.querySelector('live-color-preview');
    if (preview?.matchesSelection && !preview.matchesSelection()) {
      this.slideshow?.select(0, undefined, { animate: false });
      return;
    }
    const previewSlideIndex = Number(this.dataset.previewSlideIndex);
    if (!Number.isInteger(previewSlideIndex)) return;

    if (this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches) {
      this.refs.previewMedia?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.slideshow?.select(previewSlideIndex, undefined, { animate: false });
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {import('./events').VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const preview = this.querySelector('live-color-preview');
    const nextPreview = newMediaGallery.querySelector('live-color-preview');
    if (preview && preview.dataset.geometrySelection === nextPreview?.dataset.geometrySelection) return;

    if (nextPreview?.dataset.geometrySelection && newMediaGallery.dataset.previewSlideIndex) {
      newMediaGallery.querySelector('slideshow-component')?.setAttribute('initial-slide', newMediaGallery.dataset.previewSlideIndex);
    }
    this.replaceWith(newMediaGallery);
    if (nextPreview?.dataset.geometrySelection) queueMicrotask(() => newMediaGallery.#handleVariantSelected());
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {import('./events').ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
